import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Plane, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { api } from "../lib/utils";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [message, setMessage] = useState("");
  const verificationAttempted = useRef(false);

  useEffect(() => {
    // Prevent double execution in React Strict Mode
    if (verificationAttempted.current) return;
    verificationAttempted.current = true;

    const token = searchParams.get("token");
    
    if (!token) {
      setStatus("error");
      setMessage("Invalid verification link. No token provided.");
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await api.post(`/api/auth/verify-email?token=${token}`);
      setStatus("success");
      setMessage(response.data.message || "Email verified successfully!");
    } catch (error) {
      setStatus("error");
      setMessage(error.response?.data?.detail || "Verification failed. The link may be invalid or expired.");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Plane className="h-8 w-8 text-primary" />
          <span className="font-heading font-semibold text-xl tracking-wide">
            Cuvano® FlightCore
          </span>
        </div>

        {/* Card */}
        <div className="bg-card/50 border border-border rounded-lg p-8 text-center">
          {status === "verifying" && (
            <>
              <Loader2 className="h-12 w-12 text-primary mx-auto mb-4 animate-spin" />
              <h2 className="font-medium text-lg mb-2">Verifying your email...</h2>
              <p className="text-muted-foreground text-sm">Please wait while we verify your account.</p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="font-medium text-lg mb-2">Email Verified!</h2>
              <p className="text-muted-foreground text-sm mb-2">{message}</p>
              <p className="text-muted-foreground text-xs mb-6">
                Your account is now pending admin approval. You will be able to sign in once an administrator approves your access.
              </p>
              <Button onClick={() => navigate("/login")} className="w-full">
                Back to Sign In
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h2 className="font-medium text-lg mb-2">Verification Issue</h2>
              <p className="text-muted-foreground text-sm mb-4">{message}</p>
              <p className="text-muted-foreground text-xs mb-6">
                If you've already verified your email, try signing in. Your account may be pending admin approval.
              </p>
              <div className="space-y-2">
                <Button onClick={() => navigate("/login")} className="w-full">
                  Go to Sign In
                </Button>
                <Button variant="outline" onClick={() => navigate("/login")} className="w-full">
                  Request New Verification Link
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-zinc-500 text-xs tracking-wider text-center mt-8">
          POWERED BY CUVANO®
        </p>
      </div>
    </div>
  );
}
